import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { type FontKey, type ThemeMode, applyBrand, applyFont, applyTheme } from '../lib/brand';

interface BrandState {
  color: string;
  font: FontKey;
  theme: ThemeMode;
  missionLanguage: boolean;
}
interface BrandApi extends BrandState {
  setColor(hex: string): void;
  setFont(key: FontKey): void;
  setTheme(mode: ThemeMode): void;
  setMissionLanguage(on: boolean): void;
}
const Ctx = createContext<BrandApi | null>(null);
export function useBrand(): BrandApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useBrand() must be used inside <BrandProvider>');
  return c;
}

const THEME_KEY = 'ui-theme';
function storedTheme(): ThemeMode | null {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : null;
  } catch {
    return null;
  }
}

/** Applies tenant colour, font and light/dark theme to :root (BrandProvider initial = tenant-config). */
export function BrandProvider({ initial, children }: { initial?: Partial<BrandState>; children: ReactNode }) {
  const [s, setS] = useState<BrandState>(() => ({
    color: initial?.color ?? '#0457fd',
    font: initial?.font ?? 'jakarta',
    theme: storedTheme() ?? initial?.theme ?? 'system',
    missionLanguage: initial?.missionLanguage ?? true,
  }));
  useEffect(() => { applyBrand(s.color); }, [s.color]);
  useEffect(() => { applyFont(s.font); }, [s.font]);
  useEffect(() => {
    applyTheme(s.theme);
    if (s.theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => applyTheme('system');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [s.theme]);
  const api = useMemo<BrandApi>(
    () => ({
      ...s,
      setColor: (color) => setS((p) => ({ ...p, color })),
      setFont: (font) => setS((p) => ({ ...p, font })),
      setTheme: (theme) => {
        try { localStorage.setItem(THEME_KEY, theme); } catch { /* private mode */ }
        setS((p) => ({ ...p, theme }));
      },
      setMissionLanguage: (missionLanguage) => setS((p) => ({ ...p, missionLanguage })),
    }),
    [s],
  );
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/** UI wording follows the tenant's Mission language setting (PLAN §10A). */
export const TERMS = {
  mission: { item: 'Mission', items: 'Missions', sub: 'Objective', subs: 'Objectives', home: 'Mission Control', mine: 'My Missions', done: 'Mission accomplished', crew: 'Crew' },
  neutral: { item: 'Task', items: 'Tasks', sub: 'Subtask', subs: 'Subtasks', home: 'Home', mine: 'My Work', done: 'Task completed', crew: 'Members' },
} as const;
export function useTerms() {
  return useBrand().missionLanguage ? TERMS.mission : TERMS.neutral;
}
